import { normalizeInvoiceStatus } from "../../../utilis/invoiceStatusHelpers";
import { resolveInvoiceLineItemsFromRecord } from "../../../utilis/invoiceEditHelpers";
import {
  lineHasExplicitTaxDeclaration,
  resolveInvoiceRecordPayableTotals,
  resolveSavedLineTaxRate,
  resolveSavedLineTaxType,
} from "../../../utilis/invoiceLineItemHelpers";

export function extractNotesFromPayload(result) {
  if (Array.isArray(result?.notes) && result.notes.length) return result.notes;
  if (Array.isArray(result?.raw?.notes)) return result.raw.notes;
  if (Array.isArray(result?.response?.data?.invoice?.notes)) {
    return result.response.data.invoice.notes;
  }
  if (Array.isArray(result?.response?.data?.notes)) {
    return result.response.data.notes;
  }
  return [];
}

function extractInstallmentIdsFromPayload(result = {}) {
  const commission = [
    ...(result?.commissionInstallmentIds || []),
    ...(result?.invoice?.commissionInstallmentIds || []),
    ...(Array.isArray(result?.wrapper?.installment_ids)
      ? result.wrapper.installment_ids
      : []),
    ...(Array.isArray(result?.wrapper?.linked_installments)
      ? result.wrapper.linked_installments.map((row) => row?.id)
      : []),
    ...(Array.isArray(result?.linkedInstallments)
      ? result.linkedInstallments.map((row) => row?.id)
      : []),
  ]
    .map(Number)
    .filter(Boolean);

  const bonus = [
    ...(result?.bonusInstallmentIds || []),
    ...(result?.invoice?.bonusInstallmentIds || []),
    ...(Array.isArray(result?.wrapper?.bonus_installment_ids)
      ? result.wrapper.bonus_installment_ids
      : []),
    ...(Array.isArray(result?.wrapper?.linked_bonus_installments)
      ? result.wrapper.linked_bonus_installments.map((row) => row?.id)
      : []),
    ...(Array.isArray(result?.linkedBonusInstallments)
      ? result.linkedBonusInstallments.map((row) => row?.id)
      : []),
  ]
    .map(Number)
    .filter(Boolean);

  return {
    commission_installment_ids: [...new Set(commission)],
    bonus_installment_ids: [...new Set(bonus)],
  };
}

export function enrichLineItemsWithLinkedInstallments(lineItems = [], linkedIds = {}) {
  const lines = Array.isArray(lineItems) ? lineItems.map((row) => ({ ...row })) : [];
  const commissionQueue = [...(linkedIds.commission_installment_ids || [])]
    .map(Number)
    .filter(Boolean);
  const bonusQueue = [...(linkedIds.bonus_installment_ids || [])]
    .map(Number)
    .filter(Boolean);

  lines.forEach((line) => {
    const commissionId = Number(
      line.commission_installment_id ?? line.commissionInstallmentId,
    );
    const bonusId = Number(line.bonus_installment_id ?? line.bonusInstallmentId);
    if (commissionId) {
      const index = commissionQueue.indexOf(commissionId);
      if (index >= 0) commissionQueue.splice(index, 1);
      line.commission_installment_id = commissionId;
    }
    if (bonusId) {
      const index = bonusQueue.indexOf(bonusId);
      if (index >= 0) bonusQueue.splice(index, 1);
      line.bonus_installment_id = bonusId;
    }
  });

  return lines.map((line) => {
    if (line.commission_installment_id || line.bonus_installment_id) {
      return line;
    }
    const description = String(line.description || "");
    if (bonusQueue.length && /bonus/i.test(description)) {
      return { ...line, bonus_installment_id: bonusQueue.shift() };
    }
    if (commissionQueue.length) {
      return { ...line, commission_installment_id: commissionQueue.shift() };
    }
    if (bonusQueue.length) {
      return { ...line, bonus_installment_id: bonusQueue.shift() };
    }
    return line;
  });
}

function commissionLineItemMatchKey(row = {}, index = 0) {
  const bonusId = Number(row.bonus_installment_id ?? row.bonusInstallmentId);
  const commissionId = Number(
    row.commission_installment_id ?? row.commissionInstallmentId,
  );
  if (bonusId) return `bonus:${bonusId}`;
  if (commissionId) return `commission:${commissionId}`;
  const lineNumber = Number(row.line_number ?? row.lineNumber);
  if (lineNumber) return `line:${lineNumber}`;
  return `index:${index}`;
}

function mergeCommissionLineItemTaxFields(primaryRow = {}, alternateRows = []) {
  let merged = { ...primaryRow };
  const primaryTax = resolveSavedLineTaxType(merged);

  for (const alt of alternateRows) {
    if (!alt || typeof alt !== "object") continue;
    const altTax = resolveSavedLineTaxType(alt);
    const primaryDeclaresTax = lineHasExplicitTaxDeclaration(merged);
    const shouldUseAltTax =
      !primaryDeclaresTax &&
      ((primaryTax === "notax" && altTax !== "notax") ||
        ((Number(merged.tax_amount ?? merged.gst_amount) || 0) <= 0 &&
          (Number(alt.tax_amount ?? alt.gst_amount) || 0) > 0));

    // Keep an explicit saved tax (including notax). Bonus installment GST
    // often sits on an alternate payload as inclusive 10 and must not win.
    if (!shouldUseAltTax) continue;

    merged = {
      ...merged,
      ...alt,
      tax_type: altTax !== "notax" ? altTax : primaryTax,
      gst_type: alt.gst_type ?? alt.gstType ?? altTax,
      gst_status: alt.gst_status ?? alt.gstStatus ?? altTax,
      tax_rate:
        resolveSavedLineTaxRate(alt) ||
        resolveSavedLineTaxRate(merged) ||
        merged.tax_rate,
      gst_percentage:
        alt.gst_percentage ??
        alt.gstPercentage ??
        merged.gst_percentage ??
        merged.tax_rate,
      tax_amount: merged.tax_amount ?? alt.tax_amount ?? alt.gst_amount,
      gst_amount: merged.gst_amount ?? alt.gst_amount ?? alt.tax_amount,
      bonus_installment_id:
        merged.bonus_installment_id ??
        merged.bonusInstallmentId ??
        alt.bonus_installment_id ??
        alt.bonusInstallmentId ??
        null,
      commission_installment_id:
        merged.commission_installment_id ??
        merged.commissionInstallmentId ??
        alt.commission_installment_id ??
        alt.commissionInstallmentId ??
        null,
      fee_type: merged.fee_type || merged.feeType || alt.fee_type || alt.feeType,
    };
  }

  return merged;
}

/** Collect line items from every shape the commission invoice API may return. */
function resolveCommissionWizardLineItems(result, financeNested, raw) {
  const sources = [
    financeNested,
    raw,
    raw?.finance_invoice,
    raw?.financeInvoice,
    result?.invoice,
    result?.wrapper,
  ].filter((row) => row && typeof row === "object");

  const parsedSources = sources
    .map((source) => resolveInvoiceLineItemsFromRecord(source))
    .filter((items) => items.length > 0);

  if (!parsedSources.length) return [];

  const base = [...parsedSources].sort(
    (left, right) => right.length - left.length,
  )[0];
  const alternates = parsedSources.filter((items) => items !== base);

  const alternatesByKey = new Map();
  alternates.forEach((items) => {
    items.forEach((row, index) => {
      const key = commissionLineItemMatchKey(row, index);
      if (!alternatesByKey.has(key)) alternatesByKey.set(key, []);
      alternatesByKey.get(key).push(row);
    });
  });

  return base.map((row, index) => {
    const key = commissionLineItemMatchKey(row, index);
    return mergeCommissionLineItemTaxFields(
      row,
      alternatesByKey.get(key) || [],
    );
  });
}

/** Payment status only — approval is tracked separately via `approved`. */
export function resolveCommissionInvoicePaymentStatus(record = {}) {
  const raw = record?.status || record?.payment_status || "";
  const normalized = normalizeInvoiceStatus(raw);
  if (normalized === "approved") return "awaiting_payment";
  return normalized;
}

export function mapCommissionInvoiceToWizardRecord(result) {
  const invoice = result?.invoice;
  const raw = result?.raw || {};
  if (!invoice && !raw) return null;

  const linkedIds = extractInstallmentIdsFromPayload(result);
  const approved = Boolean(
    raw.approved ?? invoice?.approved ?? result?.approved,
  );

  const financeNested =
    raw.finance_invoice ||
    raw.financeInvoice ||
    (raw.invoice_number || raw.items ? raw : null) ||
    null;

  if (financeNested && typeof financeNested === "object") {
    const nestedLines = resolveCommissionWizardLineItems(
      result,
      financeNested,
      raw,
    );
    const nestedRecord = {
      ...financeNested,
      id: invoice?.id || raw.id || financeNested.id,
      approved,
      invoice_number:
        financeNested.invoice_number ||
        financeNested.invoiceNumber ||
        invoice?.invoiceNumber ||
        raw.invoice_number ||
        "",
      issue_date:
        financeNested.issue_date ||
        financeNested.invoice_date ||
        raw.invoice_date ||
        raw.issue_date ||
        invoice?.invoiceDate ||
        "",
      due_date:
        financeNested.due_date ||
        financeNested.dueDate ||
        raw.due_date ||
        invoice?.dueDate ||
        "",
      reference:
        financeNested.reference ||
        raw.remarks ||
        raw.reference ||
        invoice?.remarks ||
        "",
      branding_id:
        financeNested.branding_id ??
        financeNested.brandingId ??
        raw.branding_id ??
        null,
      branch_id:
        financeNested.branch_id ??
        financeNested.branchId ??
        raw.branch_id ??
        null,
      currency:
        financeNested.currency || raw.currency || invoice?.currency || "AUD",
      tax_type:
        financeNested.tax_type ??
        financeNested.gst_status ??
        raw.tax_type ??
        raw.gst_status ??
        "",
      gst_status:
        financeNested.gst_status ??
        financeNested.tax_type ??
        raw.gst_status ??
        raw.tax_type ??
        "",
      total_amount:
        financeNested.total_amount ??
        financeNested.totalAmount ??
        raw.total_amount ??
        invoice?.amount ??
        0,
      line_items: enrichLineItemsWithLinkedInstallments(nestedLines, linkedIds),
      transactions:
        financeNested.transactions ||
        financeNested.ledger_transactions ||
        raw.transactions ||
        [],
      notes: Array.isArray(financeNested.notes)
        ? financeNested.notes
        : extractNotesFromPayload(result),
      _commissionInvoiceId: invoice?.id || raw.id,
      _commissionInstallmentIds: linkedIds.commission_installment_ids,
      _bonusInstallmentIds: linkedIds.bonus_installment_ids,
      _planId:
        invoice?.invoiceSourceId ||
        raw.invoice_source_id ||
        financeNested.invoice_source_id ||
        null,
      application_id:
        financeNested.application_id ||
        raw.application_id ||
        invoice?.applicationId ||
        null,
      application_type:
        financeNested.application_type ||
        raw.application_type ||
        "education",
    };
    nestedRecord.status = resolveCommissionInvoicePaymentStatus(nestedRecord);
    const payable = resolveInvoiceRecordPayableTotals(nestedRecord);
    nestedRecord.total_amount = payable.total_amount;
    nestedRecord.amount_due = payable.amount_due;
    nestedRecord.paid_amount = payable.paid_amount;
    return nestedRecord;
  }

  const lineItems = resolveCommissionWizardLineItems(result, raw, raw);

  const flatRecord = {
    id: invoice?.id || raw.id,
    invoice_number:
      invoice?.invoiceNumber ||
      raw.invoice_number ||
      (invoice?.id != null ? `CI-${invoice.id}` : ""),
    status: invoice?.status || raw.status || "draft",
    approved,
    issue_date:
      invoice?.invoiceDate || raw.invoice_date || raw.issue_date || "",
    due_date: invoice?.dueDate || raw.due_date || "",
    reference: invoice?.remarks || raw.remarks || raw.reference || "",
    currency: invoice?.currency || raw.currency || "AUD",
    total_amount: invoice?.amount || raw.total_amount || 0,
    amount_due: raw.amount_due ?? invoice?.amount ?? 0,
    paid_amount: raw.paid_amount || 0,
    discount_total: raw.discount_total || 0,
    subtotal: raw.subtotal || invoice?.amount || 0,
    tax_amount: raw.tax_amount || 0,
    tax_type: raw.tax_type ?? raw.gst_status ?? "",
    gst_status: raw.gst_status ?? raw.tax_type ?? "",
    branding_id: raw.branding_id || null,
    branch_id: raw.branch_id || null,
    client_id: raw.client_id || null,
    application_id: raw.application_id || null,
    application_type: raw.application_type || "education",
    line_items: enrichLineItemsWithLinkedInstallments(lineItems, linkedIds),
    transactions: raw.transactions || raw.ledger_transactions || [],
    client_snapshot: raw.client_snapshot || null,
    branding_snapshot: raw.branding_snapshot || null,
    branch_snapshot: raw.branch_snapshot || null,
    notes: extractNotesFromPayload(result),
    _commissionInvoiceId: invoice?.id || raw.id,
    _commissionInstallmentIds: linkedIds.commission_installment_ids,
    _bonusInstallmentIds: linkedIds.bonus_installment_ids,
    _planId: invoice?.invoiceSourceId || raw.invoice_source_id || null,
  };
  flatRecord.status = resolveCommissionInvoicePaymentStatus(flatRecord);
  const payable = resolveInvoiceRecordPayableTotals(flatRecord);
  flatRecord.total_amount = payable.total_amount;
  flatRecord.amount_due = payable.amount_due;
  flatRecord.paid_amount = payable.paid_amount;
  return flatRecord;
}
